import { CartItem } from './types'

export interface WhatsAppOrderData {
  restaurantName: string
  whatsappNumber: string
  currencySymbol: string
  orderNumber: string
  customerName: string
  customerPhone: string
  deliveryType?: string
  paymentMethod?: string
  items: CartItem[]
  subtotal: number
  deliveryFee: number
  total: number
  deliveryAddress?: string | null
  mapsUrl?: string | null
  notes?: string | null
  ticketImageUrl?: string | null
  trackingToken: string
}

function formatDeliveryType(type?: string): string {
  switch (type) {
    case 'delivery':
      return 'Delivery'
    case 'pickup':
      return 'Para llevar'
    case 'dine_in':
      return 'Comer aquí'
    default:
      return type || 'N/A'
  }
}

function formatPaymentMethod(method?: string): string {
  switch (method) {
    case 'sinpe':
      return 'SINPE Móvil'
    case 'cash':
      return 'Efectivo'
    case 'card':
      return 'Tarjeta'
    default:
      return method || 'N/A'
  }
}

export function buildWhatsAppMessage(data: WhatsAppOrderData): string {
  const cs = data.currencySymbol
  const fmt = (n: number) => n.toLocaleString()
  const trackingUrl = `${window.location.origin}/track/${data.trackingToken}`

  // Build numbered items list
  const itemsList = data.items
    .map((item, idx) => {
      const optTotal = item.selected_options.reduce(
        (sum, opt) => sum + opt.price_delta,
        0,
      )
      const lineTotal = (item.unit_price_snapshot + optTotal) * item.quantity

      let line = `${idx + 1}. ${item.item_name_snapshot} (x${item.quantity}) — ${cs}${fmt(lineTotal)}`

      for (const opt of item.selected_options) {
        const delta =
          opt.price_delta > 0 ? ` +${cs}${fmt(opt.price_delta)}` : ''
        line += `\n   ${opt.option_name}${delta}`
      }

      if (item.notes) {
        line += `\n   📝 ${item.notes}`
      }

      return line
    })
    .join('\n\n')

  // Build message
  let msg = ''

  msg += `🔥 *NUEVO PEDIDO — ${data.restaurantName}*\n\n`

  msg += `📦 *Pedido #${data.orderNumber}*\n`
  msg += `👤 Cliente: ${data.customerName}\n`
  msg += `📞 Tel: ${data.customerPhone}\n\n`

  msg += `🛒 *PEDIDO:*\n\n`
  msg += `${itemsList}\n\n`

  msg += `💰 Subtotal: ${cs}${fmt(data.subtotal)}\n`
  if (data.deliveryFee > 0) {
    msg += `🚚 Envío: ${cs}${fmt(data.deliveryFee)}\n`
  }
  msg += `✅ *TOTAL: ${cs}${fmt(data.total)}*\n\n`

  msg += `🚚 Entrega: ${formatDeliveryType(data.deliveryType)}\n`
  msg += `💳 Pago: ${formatPaymentMethod(data.paymentMethod)}\n`

  if (data.deliveryAddress) {
    msg += `\n📍 *Dirección:*\n${data.deliveryAddress}\n`
  }
  if (data.mapsUrl) {
    msg += `🗺️ ${data.mapsUrl}\n`
  }

  msg += `\n📝 Notas: ${data.notes || 'Sin notas'}\n`

  msg += `\n🔎 *Seguimiento:*\n${trackingUrl}\n`

  if (data.ticketImageUrl) {
    msg += `\n🧾 *Ticket:*\n${data.ticketImageUrl}\n`
  }

  return msg
}

export function buildWhatsAppUrl(
  whatsappNumber: string,
  message: string,
): string {
  const cleanNumber = whatsappNumber.replace(/\D/g, '')
  return `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`
}
